import React, { FC, FormEvent, Fragment, useCallback, useContext, useEffect, useMemo } from 'react';
import { Formik, FormikProps } from 'formik';

import { DnsAcceptanceContext, DnsAcceptanceProvider } from '../context/dns-acceptance';
import { DnsAcceptanceFormData } from '../types/dns-acceptance';
import { Button, Col, Form, Input, Row, Space, Statistic, Typography, message } from 'antd';
import * as Icons from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { ParcelContext } from '@modules/parcels/contexts';
import { ParcelService } from '@modules/parcels/services';
import { PageContent } from '@shared/styled/page-content';
import { CheckboxField } from '@shared/modules/form/fields/checkbox';

const FormikComponent: FC<FormikProps<DnsAcceptanceFormData>> = ({ isSubmitting, values, handleSubmit, handleChange, handleBlur }) => {
  const { t } = useTranslation();
  const { currentParcelFields, customErrors } = useContext(DnsAcceptanceContext);
  const { currentParcel } = useContext(ParcelContext);

  useEffect(() => {
    localStorage.setItem("acceptance.autoPrint", values.autoPrint ? "1" : '2')
  }, [values.autoPrint])

  useEffect(() => {
    localStorage.setItem("branch_check", JSON.stringify(values.branchCheck))
  }, [values.branchCheck])

  const onRemoveFromParcel = useMutation({
    mutationFn: async (declaration_id: string | number) => {
      message.loading('Əməliyyat aparılır...');
      const response = await ParcelService.removeFromParcel(declaration_id);
      if (response.status === 200) {
        message.success('Əməliyyat müvəffəqiyyətlə başa çatdı');
      } else {
        message.error(response.data)
      }
      message.destroy()
    }
  })

  const customHandleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    handleSubmit();
  }, [handleSubmit])

  const customBorder = useMemo(() => {
    let color = 'white';
    if (customErrors === "no") color = '#2ecc71';
    if (customErrors === "yes") color = '#e74c3c';
    if (customErrors === "warning") color = '#f39c12';
    return `3px solid ${color}`;
  }, [customErrors])


  return (
    <Row gutter={[16, 16]}>
      <Col xs={24}>
        <PageContent title={t('dns.autoAcceptance.autoAcceptance')}>
          {!currentParcel && <Typography.Text>{t('dns.autoAcceptance.parcelRequired')}</Typography.Text>}
          <form onSubmit={customHandleSubmit}>
            <Form style={{ marginBottom: -24 }} layout='vertical' size='large' component='div'>
              <div>
                <Input.Search
                  prefix={<Icons.BarcodeOutlined style={{ marginRight: 8, color: '#999' }} />}
                  autoFocus={true}
                  autoComplete='off'
                  name='trackCode'
                  disabled={!currentParcel}
                  placeholder={t('declarations.fields.trackCode')}
                  value={values.trackCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onSubmit={customHandleSubmit}
                  loading={isSubmitting}
                />
              </div>
              <div>
                <Space size={12}>
                  <CheckboxField name='autoPrint' input={{ children: t('dns.autoAcceptance.autoPrint'), disabled: !currentParcel }} />
                  <CheckboxField name='branchCheck' input={{ children: t('dns.autoAcceptance.branchCheck'), disabled: !currentParcel }} />
                </Space>
              </div>
            </Form>
          </form>
        </PageContent>
      </Col>
      {currentParcel && (
        <Fragment>
          <Col xs={8}>
            <PageContent style={{ border: customBorder }} title={t('parcels.currentWithName', { name: currentParcel.name })}>
              <Row>
                <Col xs={6}>
                  <Space size={32}>
                    <Statistic title={t('declarations.totalWeight')} value={currentParcelFields?.weight} precision={2} suffix='kq' />
                  </Space>
                </Col>
                <Col>
                  <Space size={32}>
                    <Statistic title={t('declarations.totalCount')} value={currentParcelFields?.count} precision={0} suffix='ədəd' />
                  </Space>
                </Col>
              </Row>
            </PageContent>
          </Col>
          {currentParcelFields.data &&
            <Col xs={16}>
              <PageContent title={t('declarations.declarations', { name: currentParcel.name })}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("declarations.fields.trackCode")}</th>
                      <th>{t("common.fields.user.name")}</th>
                      <th>{t("declarations.fields.prevBasket")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentParcelFields.data.map(elem =>
                      <tr key={elem.id}>
                        <td>{elem.track_code}</td>
                        <td>{elem.user_name}</td>
                        <td>{elem.basket_name_tmp}</td>
                        <td>
                          <Button onClick={() => onRemoveFromParcel.mutate(elem.id)}>
                            {t("declarations.actions.removeFromParcel")}
                          </Button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </PageContent>
            </Col>}
        </Fragment>
      )
      }
    </Row >
  );
};

export const DnsTasksAcceptance = () => {
  return (
    <Fragment>
      <style>
        {`
      .modal_content_big .ant-modal-confirm-content{
        font-size: 20px;
}`}
      </style>
      <DnsAcceptanceProvider>
        <DnsAcceptanceContext.Consumer>
          {({ initialValues, onSubmit }) => <Formik initialValues={initialValues} onSubmit={onSubmit} component={FormikComponent} />}
        </DnsAcceptanceContext.Consumer>
      </DnsAcceptanceProvider>
    </Fragment>
  );
};
